import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import IconRight from '@/icons/IconRight.svg?react';
import type { FindEventsCardProps } from '@/types/events';

/**
 * FindEventsCard - Sidebar promotional card that helps users find local events
 *
 * Displays a content image, headline, body text, and text links with arrow icons
 * pointing to external resources (AAAs, PACE centers, libraries).
 * Follows the Sidebar Info Cards pattern with white background, #EBEBEB border,
 * 4px border-radius, and 16px padding.
 *
 * @example
 * ```tsx
 * <FindEventsCard
 *   content={{
 *     image: { src: "/images/events/FindEventsCard-Image.png", alt: "..." },
 *     title: "Find events happening near you",
 *     description: "Explore your local Area Agency on Aging...",
 *     links: [
 *       { text: "Area Agencies on Aging (AAAs)", href: "https://aging.ohio.gov/agencies" }
 *     ]
 *   }}
 * />
 * ```
 */
export const FindEventsCard = forwardRef<HTMLElement, FindEventsCardProps>(
  ({ content, className }, ref) => {
    if (!content) {
      return null;
    }

    const { image, title, description, links } = content;

    return (
      <aside
        ref={ref}
        className={cn(
          'rounded-[4px] border border-[#EBEBEB] bg-background p-4 flex flex-col gap-3',
          className
        )}
        style={{
          boxShadow: '0px 4px 4px 0px rgba(187, 187, 187, 0.25)',
        }}
      >
        {/* Content Image */}
        {image && (
          <img
            src={image.src}
            alt=""
            className="w-full rounded-[4px] object-cover"
          />
        )}

        {/* Title */}
        <h3 className={cn(typography.headingLarge, 'text-foreground m-0')}>
          {title}
        </h3>

        {/* Description */}
        <p className={cn(typography.body, 'text-foreground m-0')}>
          {description}
        </p>

        {/* Links */}
        {links && links.length > 0 && (
          <ul className="list-none m-0 p-0 flex flex-col gap-2">
            {links.map((link) => (
              <li key={link.href} className="m-0 p-0">
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    typography.subtitle,
                    'text-[var(--color-primary-brand)] no-underline hover:underline inline-flex items-center gap-2',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-brand)] rounded-sm'
                  )}
                >
                  {link.text}
                  <IconRight className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </aside>
    );
  }
);

FindEventsCard.displayName = 'FindEventsCard';
